import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-cropper';
import { toast } from 'react-hot-toast';

const LogoCropper = ({ image, onCropComplete, onCancel, loading = false }) => {
  const { t } = useTranslation();
  const cropperRef = useRef();

  const handleSave = async () => {
    console.log('Save button clicked');
    
    if (!cropperRef.current) {
      toast.error(t('settings.cropper_not_ready', 'Cropper not ready'));
      return;
    }

    try {
      const cropper = cropperRef.current.cropper;
      
      if (!cropper) {
        toast.error(t('settings.cropper_not_initialized', 'Cropper not initialized'));
        return;
      }

      console.log('Getting cropped canvas...');
      const canvas = cropper.getCroppedCanvas({
        width: 512, // Max width for logo
        height: 512, // Max height for logo
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
        fillColor: '#ffffff', // White background for transparent images
      });

      if (!canvas) {
        toast.error(t('settings.crop_failed', 'Failed to create cropped image'));
        return;
      }

      console.log('Converting canvas to blob...');
      canvas.toBlob((blob) => {
        if (blob) {
          console.log('Cropped image created successfully, size:', blob.size);
          // Don't show success message here - let the parent handle it
          onCropComplete(blob);
        } else {
          console.error('Failed to create blob from canvas');
          toast.error(t('settings.crop_failed', 'Failed to process cropped image'));
        }
      }, 'image/png', 0.9);

    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error(t('settings.crop_failed', 'Failed to crop image: ') + error.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('settings.crop_logo', 'Crop Logo')}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="relative w-full h-96 bg-gray-100 border-2 border-gray-200 rounded-lg overflow-hidden mb-4">
          <Cropper
            src={image}
            style={{ height: '100%', width: '100%' }}
            ref={cropperRef}
            // Cropper configuration
            aspectRatio={null} // Free aspect ratio - any shape
            guides={true} // Show grid lines
            center={true} // Center the crop box initially
            highlight={true} // Highlight the crop area
            background={true} // Show background
            autoCrop={true} // Enable crop box on initialization
            checkCrossOrigin={false}
            checkOrientation={false}
            modal={true} // Show the dark mask around crop area
            
            // Movement and scaling options
            dragMode="move" // Drag to move the image
            viewMode={1} // Restrict crop box to not exceed canvas
            scalable={true} // Allow scaling the image
            zoomable={true} // Allow zooming
            rotatable={false} // Disable rotation for simplicity
            
            // Crop box options
            cropBoxMovable={true} // Allow moving the crop box
            cropBoxResizable={true} // Allow resizing the crop box - THIS IS KEY!
            toggleDragModeOnDblclick={false}
            autoCropArea={0.9} // Start with 90% of the image
          />
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            {t('settings.crop_help', 'Drag the corners and edges to resize the crop area. You can make any shape or size you want.')}
          </p>
          <p className="text-sm text-blue-600 mb-2">
            ✨ <strong>Now with real corner handles!</strong> Drag the corner squares to resize, drag inside to move.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <h4 className="font-medium text-blue-900 mb-1">How to use:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Resize:</strong> Drag the corner squares or edge lines</li>
              <li>• <strong>Move:</strong> Drag inside the crop area</li>
              <li>• <strong>Zoom:</strong> Use mouse wheel to zoom in/out</li>
              <li>• <strong>Pan:</strong> Drag outside the crop area to move the image</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('common.saving', 'Saving...')}
              </span>
            ) : (
              t('common.save', t('common.save'))
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoCropper; 